<template>
  <div class="frequency-content">
    <mds-input
      v-model="name"
      :label="translate('frequency.modal.name.label')"
      :placeholder="translate('frequency.modal.name.placeholder')"
      :error="!!error.length"
      :error-text="[error]"
    />
    <frequency-recurrence v-on="$listeners" />
    <start-date
      :label="translate('frequency.modal.date.label')"
      :startDate="startDate"
      @input="
        $emit('input', $event)
        startDate = $event.startDay
      "
      @validDate="$emit('validDate', $event)"
    />
    <Time
      :time="time"
      :timezones="timezones"
      :is-same-day="sameDay"
      :repeat-type="repeatType"
      :start-date="startDate"
      :timezone="timezone"
      @input="$emit('input', $event)"
      @error="$emit('error', $event)"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import MdsInput from '@mds/input'
import { timeZones } from '@/utils/getter'
import { format, zonedTimeToUtc } from 'date-fns-tz'
import { formatDate, USER_PREFERENCES_TIMEZONE } from '@/utils/date'
import { isNameInvalid } from '@/utils/nameValidation'
import FrequencyRecurrence from '@/components/frequencyModal/frequencies/FrequencyRecurrence.vue'
import Time from '@/components/frequencyModal/frequencies/helpers/Time.vue'
import StartDate from '@/components/frequencyModal/frequencies/helpers/StartDate.vue'
import MwcBaseHelpersMixin from '@morningstar/mcl-utilities/src/vue/mixins/mwc-base-mixin'
import defaultConfig from '@/components/frequencyModal/config/i18n'

export default {
  name: 'FrequencyModalContent',
  components: {
    MdsInput,
    Time,
    FrequencyRecurrence,
    StartDate,
  },
  props: {
    frequencyForm: {
      type: Object,
      default: /* istanbul ignore next */ () => ({}),
    },
    withoutJobName: Boolean,
  },
  mixins: [MwcBaseHelpersMixin],
  data() {
    return {
      defaultConfig,
      dataSet: [],
      startDate: format(new Date(), 'yyyy-MM-dd'),
      time: '',
      timezone: USER_PREFERENCES_TIMEZONE,
      repeatType: 'OnlyOnce',
      timezones: timeZones,
      name: '',
      error: [],
    }
  },
  computed: {
    ...mapState({
      editedSchedule: (state) => state.cache.cache.editedSchedule,
      editedFeed: (state) => state.cache.cache.editedFeed,
    }),
    sameDay() {
      const date = format(new Date(), 'yyyy-MM-dd')
      return this.startDate === date
    },
    randomNumber() {
      return Math.floor(Math.random() * 1001)
    },
  },
  watch: {
    name: {
      deep: true,
      immediate: true,
      handler: 'validateJobName',
    },
    withoutJobName: {
      deep: true,
      immediate: true,
      handler: 'validateJobName',
    },
    frequencyForm: {
      deep: true,
      immediate: true,
      handler() {
        this.repeatType = this.frequencyForm.repeatType
      },
    },
    editedSchedule: {
      deep: true,
      immediate: true,
      handler(newValue) {
        if (!isEmpty(newValue)) {
          this.setEditSchedule()
        }
      },
    },
    editedFeed: {
      deep: true,
      immediate: true,
      handler(feed) {
        if (!isEmpty(feed) && isEmpty(this.editedSchedule)) {
          const { name = ' ' } = feed
          this.name = `${String(name)}_Schedule_${String(this.randomNumber)}`
        }
      },
    },
  },
  methods: {
    async validateJobName() {
      if (this.withoutJobName) {
        const invalidScheduleName = await isNameInvalid(this.name)
        await this.$nextTick()

        const CONDITIONS = {
          [this.name.length > 200]: this.translate(
            'frequency.modal.overflow.name.error'
          ),
          [this.name.length < 4]: this.translate(
            'frequency.modal.underflow.name.error'
          ),
          [this.name.length === 0]: this.translate(
            'frequency.modal.empty.name.error'
          ),
          [invalidScheduleName]: this.translate(
            'frequency.modal.invalid.name.error'
          ),
        }

        this.error = CONDITIONS[true] || []

        if (this.error.length) {
          this.$emit('validName', false)
        } else {
          this.$emit('input', { name: this.name })
          this.$emit('validName', true)
        }
      }
      this.$emit('name', this.name)
    },
    setEditSchedule() {
      this.name = this.editedSchedule?.name ?? ''
      this.startDate =
        this.editedSchedule?.start_day ?? format(new Date(), 'yyyy-MM-dd')
      this.time = this.editedSchedule?.running_time ?? ''

      if (this.editedSchedule && this.editedSchedule?.timezone) {
        const { time, date } = formatDate(
          zonedTimeToUtc(
            `${this.editedSchedule?.start_day} ${this.editedSchedule?.running_time}`,
            this.editedSchedule?.timezone
          )
        )
        this.startDate = date
        this.time = time
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@mds/constants';
@import '@mds/typography';

.frequency-content {
  display: flex;
  flex-direction: column;
  flex: auto;
  gap: $mds-space-1-and-a-half-x;
  min-height: 100px;
  height: 100px;
  margin-bottom: $mds-space-2-and-a-half-x;
  overflow-y: auto;

  &::v-deep [class^='mds-label__text'] {
    margin-bottom: $mds-space-1-x;
  }
}
</style>
